import React, { useRef } from "react";
import styled from "styled-components";
import { toPng } from "html-to-image";

import {
  Column,
  Row,
  Label,
  H4,
  P,
  Footer,
  MainWrapper,
  Metadata,
  Spinner,
  Anchor,
  H1,
  H2,
  Button,
  RowToColumn,
  SmothLineChart,
  SubscribeBanner,
  TweetHighlight,
  DateRange,
} from "../../components";
import {
  formatShortDates,
  formatNumber,
  formatShortDatesFromTo,
} from "../../utils";
import {
  HeaderAndDrawer,
  Box,
  InfluencerList,
  HiddenComponent,
  FlagList,
  Share,
} from "./components";
import { DonutChart } from "./components/DonutChart";
import { Color, deviceMax } from "../../utils/variables";

const DownloadImage = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 372px;
  height: 272px;
  border-radius: 10px;
  background: url("/images/congress/download-image.svg");
  background-size: cover;
  background-position: center;
  position: relative;
`;

const DownloadImageHeader = styled.div`
  padding: 20px;
`;

const DownloadImageHeaderText = styled(H4)`
  color: white;
  background: ${Color("green", "6")};
  display: inline-block;
  padding: 0 5px 3px;
`;

const DownloadImageFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 20px;
  gap: 10px;
  display: flex;
  justify-content: center;

  button {
    padding: 0 40px;
  }
`;

const HiddenImage = styled.div`
  height: 0;
  overflow: hidden;
  background: white;
`;

const DonutChartWrapper = styled.div`
  @media ${deviceMax.mobileL} {
    margin: 0 -15px;
  }
`;

export const view = ({
  isAdmin,
  signUpClick,
  influentials,
  prolificTweeters,
  highestImpressions,
  tweet,
  isLoadingTweets,
  conference,
  conferenceSuccess,
  conferenceByCountry,
  loadingConferenceByCountry,
  influencerStats,
  loadingInfluencerStats,
  timeSegmentedTweets,
  loadingTimeSegmentedTweets,
  conferenceDates,
  handleDateChange,
  conferenceMonitorDates,
  selectedDates,
  maxResult,
  setMaxResult,
  ingestionTweetCount,
  ingestionAccountCount,
  ingestionTweetsPerHour,
  sameDate,
}) => {
  const imageRef = useRef(null);

  const htmlToImageConvert = () => {
    toPng(imageRef.current, { cacheBust: false })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = "#" + conference?.[0]?.tag?.name + ".png";
        link.href = dataUrl;

        // For iOS devices, we need to handle the download differently
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
          // Create a temporary link and trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          link.click();
        }
      })
      .catch((err) => {
        console.error("Error generating image:", err);
        alert("There was an error generating the image. Please try again.");
      });
  };

  return (
    <MainWrapper className="dashboard">
      <Metadata />
      <HeaderAndDrawer
        {...{ signUpClick, isAdmin, activeUrl: "congress_influencers" }}
      />
      <Column marginTop="100">
        <Column paddingBottom="100">
          <Column center>
            <Column maxWidth="1200" paddingX>
              <Column>
                <Row>
                  <Anchor
                    bold
                    icon="arrowLeft"
                    to="/influencers/hashtag"
                    label="Back to top conferences"
                  />
                </Row>
                {conference?.[0]?.tag?.name && (
                  <H2 bold>#{conference[0].tag.name}</H2>
                )}
                <RowToColumn gap="40">
                  <Column gap="20" marginTop="20" stretch minHeight="272">
                    <Column fit>
                      {conferenceSuccess && (
                        <P large color="gray" fade="7">
                          {conference?.[0]?.description ||
                            "Discover what healthcare providers are saying on social media"}
                        </P>
                      )}
                      <Row gap="20" middle marginTop="10">
                        <Label bold color="gray" fade="7">
                          {conferenceDates?.start &&
                            formatShortDates(
                              conferenceDates.start,
                              conferenceDates.end
                            )}
                        </Label>
                        {conference?.[0]?.url && (
                          <Anchor
                            bold
                            blank
                            to={conference?.[0]?.url}
                            label={conference?.[0]?.url
                              .replace("http://", "")
                              .replace("https://", "")
                              .replace(/\/$/, "")}
                          />
                        )}
                      </Row>
                    </Column>
                    {selectedDates?.start && (
                      <RowToColumn gap="20" columnGap="30">
                        <Label inline>
                          {`Showing tag activity `}
                          <Label bold>
                            {formatShortDatesFromTo(
                              selectedDates.start,
                              selectedDates.end
                            )}
                          </Label>
                        </Label>

                        <Row marginTop="-10">
                          <DateRange
                            initialDate={selectedDates.start}
                            endDate={selectedDates.end}
                            onChange={handleDateChange}
                            noBackground
                            period
                            customDates={{
                              startDate: conferenceMonitorDates.start,
                              endDate: conferenceMonitorDates.end,
                            }}
                            customPeriod={[
                              { id: "4", label: "Conference dates" },
                            ]}
                            customToggle={
                              <Button
                                secondary
                                label="Change dates"
                                icon="arrowDropDown"
                                reverse
                              />
                            }
                          />
                        </Row>
                      </RowToColumn>
                    )}
                  </Column>

                  <Column>
                    <DownloadImage>
                      <DownloadImageHeader>
                        <DownloadImageHeaderText bold>
                          Conference Stats
                        </DownloadImageHeaderText>
                      </DownloadImageHeader>
                      <DownloadImageFooter>
                        <Label color="gray" fade="7" bold>
                          Get conference stats as an image
                        </Label>
                        <Button
                          label="Download"
                          disabled={
                            influentials?.isLoading ||
                            prolificTweeters?.isLoading ||
                            highestImpressions?.isLoading ||
                            loadingConferenceByCountry ||
                            loadingInfluencerStats ||
                            loadingTimeSegmentedTweets
                          }
                          onClick={htmlToImageConvert}
                        />
                      </DownloadImageFooter>
                    </DownloadImage>

                    <Row marginTop="20">
                      <Share title={conference?.[0]?.tag?.name} />
                    </Row>
                  </Column>
                </RowToColumn>
              </Column>

              <Row expand marginTop="40">
                {loadingInfluencerStats ? (
                  <Row marginY="40" expand center>
                    <Spinner />
                  </Row>
                ) : (
                  <Row gap="40" flexWrap>
                    <Box title="Impressions" width="166" height="136">
                      <H1 bold color="green" fade="8">
                        {formatNumber(
                          influencerStats?.postCount *
                            influencerStats?.followers
                        )}
                      </H1>
                    </Box>
                    <Box title="Posts on X" width="166" height="136">
                      <H1 bold color="green" fade="8">
                        {influencerStats?.postCount >= ingestionTweetCount
                          ? formatNumber(influencerStats?.postCount)
                          : formatNumber(ingestionTweetCount)}
                      </H1>
                    </Box>
                    <Box title="Participants" width="166" height="136">
                      <H1 bold color="green" fade="8">
                        {influencerStats?.accountCount >= ingestionAccountCount
                          ? formatNumber(influencerStats?.accountCount)
                          : formatNumber(ingestionAccountCount)}
                      </H1>
                    </Box>
                    <Box
                      title="Average posts per hour"
                      width="166"
                      height="136"
                    >
                      <H1 bold color="green" fade="8">
                        {influencerStats?.avgPostsPerHour >=
                        ingestionTweetsPerHour
                          ? formatNumber(influencerStats?.avgPostsPerHour)
                          : formatNumber(ingestionTweetsPerHour)}
                      </H1>
                    </Box>
                    <Box title="Average posts per HCP" width="166" height="136">
                      <H1 bold color="green" fade="8">
                        {formatNumber(influencerStats?.avgPostsPerAccount)}
                      </H1>
                    </Box>
                  </Row>
                )}
              </Row>

              <Column gap="40">
                <Column>
                  {conference?.description && (
                    <P color="gray" fade="7">
                      {conference?.description}
                    </P>
                  )}
                  {conference?.description && (
                    <Column marginTop="10" gap="10">
                      <Label color="gray" fade="7">
                        {conference?.description}
                      </Label>
                      <Row gap="10" middle>
                        <Label bold color="gray" fade="7">
                          {conferenceDates?.start &&
                            `Conference dates (${formatShortDates(
                              conferenceDates.start,
                              conferenceDates.end
                            )})`}
                        </Label>
                        {conference?.url && (
                          <Anchor
                            bold
                            blank
                            to={conference.url}
                            label={conference.url
                              .replace("http://", "")
                              .replace("https://", "")
                              .replace(/\/$/, "")}
                          />
                        )}
                      </Row>
                    </Column>
                  )}
                </Column>

                <RowToColumn gap="40" expand>
                  <Column gap="40" stretch maxWidth="560">
                    <InfluencerList
                      title="Top influencers"
                      subtitle="Top accounts on X most mentioned by their peers"
                      influencers={influentials?.data}
                      isLoading={influentials?.isLoading}
                      isSuccess={influentials?.isSuccess}
                      counter="mentions"
                      {...{ maxResult, setMaxResult }}
                    />

                    <InfluencerList
                      title="Highest impressions"
                      subtitle="Top accounts on X whose posts generate the most views"
                      influencers={highestImpressions?.data}
                      isLoading={highestImpressions?.isLoading}
                      isSuccess={highestImpressions?.isSuccess}
                      counter="impressions"
                      {...{ maxResult, setMaxResult }}
                    />

                    {isLoadingTweets && (
                      <Row marginY="40" expand center>
                        <Spinner />
                      </Row>
                    )}
                    {tweet && (
                      <Column>
                        <Column gap="5" marginBottom="20">
                          <Row>
                            <Row bkg="green" fade="1" paddingX="5" paddingY="3">
                              <Label small bold color="purple" fade="8">
                                TOP POST FROM {conference?.[0]?.tag?.name}
                              </Label>
                            </Row>
                          </Row>
                          <Label large>
                            {tweet?.tAccount?.lastName &&
                              `Congratulations Dr. ${tweet.tAccount.lastName}. Keep up the good work!`}{" "}
                          </Label>
                        </Column>
                        <TweetHighlight
                          data={tweet}
                          to={`/post/${tweet.externalId}`}
                        />
                      </Column>
                    )}
                  </Column>

                  <Column gap="40" stretch maxWidth="560">
                    <InfluencerList
                      title="Most active posters"
                      subtitle="Top accounts on X with the highest number of posts"
                      influencers={prolificTweeters?.data}
                      isLoading={prolificTweeters?.isLoading}
                      isSuccess={prolificTweeters?.isSuccess}
                      counter="posts"
                      {...{ maxResult, setMaxResult }}
                    />
                    {loadingConferenceByCountry ? (
                      <Row marginY="40" expand center>
                        <Spinner />
                      </Row>
                    ) : (
                      <>
                        {conferenceByCountry?.length > 0 && (
                          <Box title="Posts per country">
                            <DonutChartWrapper>
                              <DonutChart
                                data={conferenceByCountry}
                                id="mainDonutChart"
                                size={400}
                              />
                            </DonutChartWrapper>
                            <FlagList data={conferenceByCountry} />
                          </Box>
                        )}
                      </>
                    )}
                    {loadingTimeSegmentedTweets ? (
                      <Row marginY="40" expand center>
                        <Spinner />
                      </Row>
                    ) : (
                      <>
                        {timeSegmentedTweets?.length > 0 && (
                          <Box
                            className="chart-box"
                            bkg
                            title={
                              sameDate
                                ? "HCP posts per hour"
                                : "HCP posts per day"
                            }
                          >
                            <Column marginBottom="-20" marginX="-10">
                              <SmothLineChart
                                sameDate={sameDate}
                                data={timeSegmentedTweets}
                                id="mainLineChart"
                                size={400}
                              />
                            </Column>
                          </Box>
                        )}
                      </>
                    )}

                    <SubscribeBanner
                      events={{
                        page: "Conference hashtag",
                        name: conference?.[0]?.tag?.name,
                      }}
                    />
                  </Column>
                </RowToColumn>
              </Column>
            </Column>
          </Column>
        </Column>
        <Footer />
      </Column>

      <HiddenImage>
        <Column ref={imageRef}>
          <HiddenComponent
            influentials={influentials}
            prolificTweeters={prolificTweeters}
            highestImpressions={highestImpressions}
            {...{
              influencerStats,
              conference,
              conferenceDates,
              conferenceByCountry,
              loadingConferenceByCountry,
              timeSegmentedTweets,
              loadingTimeSegmentedTweets,
              ingestionTweetCount,
              ingestionAccountCount,
              ingestionTweetsPerHour,
              sameDate,
            }}
          />
        </Column>
      </HiddenImage>
    </MainWrapper>
  );
};
